import { type ViewerScriptFlowAPI } from '@wix/yoshi-flow-editor';
import { getPostFeedMetadataPage } from '@wix/ambassador-blog-frontend-adapter-public-v2-post-feed-page/http';
import { getPostPageMetadata } from '@wix/ambassador-blog-frontend-adapter-public-v2-post-page/http';
import { type PlatformApi } from '@app/external/common/controller/platform-api';
import { isEditorOrPreview } from './selectors';

interface FetchMetadataParams {
  flowAPI: ViewerScriptFlowAPI;
  postSlug?: string;
  postId?: string;
  platformApi: PlatformApi;
}

interface MetadataResponse {
  viewCount: number;
  likeCount: number;
  totalComments: number;
  averageRating: number;
  totalRatings: number;
}

export const fetchMetadata = async ({
  flowAPI,
  postSlug,
  postId,
  platformApi,
}: FetchMetadataParams): Promise<MetadataResponse> => {
  try {
    if (!postSlug) {
      throw new Error('No post slug provided');
    }

    const { httpClient, errorHandler, environment } = flowAPI;
    const { withErrorHandler, getResolvedError } = errorHandler;

    if (isEditorOrPreview(platformApi)) {
      try {
        const {
          data: { postFeedMetadataPage },
        } = await withErrorHandler(
          () =>
            httpClient.request(
              getPostFeedMetadataPage({
                page: 1,
                pageSize: 1,
                languageCode: environment.language,
              }),
            ),
          { errorCodesMap: {} },
        );

        const metaDataFromFeed = postFeedMetadataPage?.postMetrics?.[postId as string];

        return {
          viewCount: metaDataFromFeed?.views ?? 0,
          likeCount: metaDataFromFeed?.likes ?? 0,
          totalComments: metaDataFromFeed?.comments ?? 0,
          averageRating: metaDataFromFeed?.averageRating ?? 0,
          totalRatings: metaDataFromFeed?.totalRatings ?? 0,
        };
      } catch (error) {
        getResolvedError(error);
        throw error;
      }
    }

    try {
      const {
        data: { postPageMetadata },
      } = await withErrorHandler(
        () =>
          httpClient.request(
            getPostPageMetadata({
              postId: postSlug,
              skipViewCountIncrement: true,
              languageCode: environment.language,
            }),
          ),
        { errorCodesMap: {} },
      );

      return postPageMetadata?.postMetadata as MetadataResponse;
    } catch (error) {
      getResolvedError(error);
      throw error;
    }
  } catch (err) {
    if (err instanceof Error) {
      flowAPI.reportError(err);
    }
    return {
      averageRating: 0,
      likeCount: 0,
      totalComments: 0,
      totalRatings: 0,
      viewCount: 0,
    };
  }
};
